import PropTypes from 'prop-types';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import useAuth from '../../hooks/useAuth';
import SpotlightDialog from './SpotlightDialog';

// ----------------------------------------------------------------------
// Ctrl+K / Cmd+K spotlight for org admins. Mounted once in OrgLayout; the
// header trigger (and anything else) opens it through useSpotlight().

const SpotlightContext = createContext(null);

/** { open, openSpotlight(query?), close } inside OrgLayout, null elsewhere. */
export const useSpotlight = () => useContext(SpotlightContext);

const isEditable = (el) =>
  !!el &&
  (el.isContentEditable ||
    ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName) ||
    el.getAttribute?.('role') === 'textbox');

SpotlightProvider.propTypes = { children: PropTypes.node };

export default function SpotlightProvider({ children }) {
  const { user } = useAuth();
  const enabled = user?.role === 'org';
  const [open, setOpen] = useState(false);
  const [initialQuery, setInitialQuery] = useState('');

  const openSpotlight = useCallback((q) => {
    setInitialQuery(typeof q === 'string' ? q : '');
    setOpen(true);
  }, []);
  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!enabled) return undefined;
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey && e.key?.toLowerCase() === 'k') {
        // Beat the browser's own Ctrl+K (address bar search) everywhere, even in inputs.
        e.preventDefault();
        setInitialQuery('');
        setOpen((o) => !o);
      } else if (e.key === '/' && !e.metaKey && !e.ctrlKey && !e.altKey && !isEditable(document.activeElement)) {
        e.preventDefault();
        setInitialQuery('');
        setOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enabled]);

  const value = useMemo(
    () => (enabled ? { open, openSpotlight, close } : null),
    [enabled, open, openSpotlight, close]
  );

  return (
    <SpotlightContext.Provider value={value}>
      {children}
      {enabled && open && <SpotlightDialog open={open} onClose={close} initialQuery={initialQuery} />}
    </SpotlightContext.Provider>
  );
}
